import React, { useState, useEffect } from 'react';
import { X, ChevronUp, ChevronDown, RotateCcw } from 'lucide-react';

const ColumnPicker = ({ allColumns, visibleColumns, defaultColumns, onApply, onClose }) => {
  // Local state: ordered list of { key, enabled }
  const [items, setItems] = useState([]);

  useEffect(() => {
    // Build ordered list: enabled columns first (in order), then disabled ones
    const enabledSet = new Set(visibleColumns);
    const ordered = [];

    // Add enabled columns in their current order
    visibleColumns.forEach(key => {
      const col = allColumns.find(c => c.key === key);
      if (col) ordered.push({ key, enabled: true });
    });

    // Add disabled columns in registry order
    allColumns.forEach(col => {
      if (!enabledSet.has(col.key)) {
        ordered.push({ key: col.key, enabled: false });
      }
    });

    setItems(ordered);
  }, [allColumns, visibleColumns]);

  const getLabel = (key) => allColumns.find(c => c.key === key)?.label || key;

  const toggleItem = (key) => {
    setItems(prev => prev.map(item =>
      item.key === key ? { ...item, enabled: !item.enabled } : item
    ));
  };

  const moveItem = (index, direction) => {
    const newItems = [...items];
    const targetIndex = index + direction;
    // Only allow reordering within enabled items
    if (targetIndex < 0 || targetIndex >= newItems.length) return;
    if (!newItems[index].enabled || !newItems[targetIndex].enabled) return;
    [newItems[index], newItems[targetIndex]] = [newItems[targetIndex], newItems[index]];
    setItems(newItems);
  };

  const handleReset = () => {
    const defaultSet = new Set(defaultColumns);
    const ordered = [];
    defaultColumns.forEach(key => {
      ordered.push({ key, enabled: true });
    });
    allColumns.forEach(col => {
      if (!defaultSet.has(col.key)) {
        ordered.push({ key: col.key, enabled: false });
      }
    });
    setItems(ordered);
  };

  const handleApply = () => {
    const enabled = items.filter(i => i.enabled).map(i => i.key);
    if (enabled.length === 0) return; // Don't allow empty
    onApply(enabled);
    onClose();
  };

  const enabledItems = items.filter(i => i.enabled);
  const disabledItems = items.filter(i => !i.enabled);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h3 className="text-lg font-semibold text-gray-900">Choose Columns</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-1">
          {/* Enabled columns */}
          {enabledItems.length > 0 && (
            <div className="mb-3">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Visible Columns</p>
              {enabledItems.map((item, idx) => {
                const globalIdx = items.indexOf(item);
                return (
                  <div key={item.key} className="flex items-center gap-2 py-1.5 px-2 rounded hover:bg-gray-50 group">
                    <input
                      type="checkbox"
                      checked={true}
                      onChange={() => toggleItem(item.key)}
                      className="h-4 w-4 rounded border-gray-300 text-yellow-600 focus:ring-yellow-500"
                    />
                    <span className="flex-1 text-sm text-gray-800">{getLabel(item.key)}</span>
                    <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => moveItem(globalIdx, -1)}
                        disabled={idx === 0}
                        className="p-0.5 rounded hover:bg-gray-200 disabled:opacity-30"
                      >
                        <ChevronUp className="h-4 w-4 text-gray-500" />
                      </button>
                      <button
                        onClick={() => moveItem(globalIdx, 1)}
                        disabled={idx === enabledItems.length - 1}
                        className="p-0.5 rounded hover:bg-gray-200 disabled:opacity-30"
                      >
                        <ChevronDown className="h-4 w-4 text-gray-500" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Disabled columns */}
          {disabledItems.length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Available Columns</p>
              {disabledItems.map((item) => (
                <div key={item.key} className="flex items-center gap-2 py-1.5 px-2 rounded hover:bg-gray-50">
                  <input
                    type="checkbox"
                    checked={false}
                    onChange={() => toggleItem(item.key)}
                    className="h-4 w-4 rounded border-gray-300 text-yellow-600 focus:ring-yellow-500"
                  />
                  <span className="flex-1 text-sm text-gray-500">{getLabel(item.key)}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-4 py-3 border-t bg-gray-50">
          <button onClick={handleReset} className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-800">
            <RotateCcw className="h-3.5 w-3.5" /> Reset to Defaults
          </button>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded">
              Cancel
            </button>
            <button
              onClick={handleApply}
              disabled={enabledItems.length === 0}
              className="px-3 py-1.5 text-sm bg-yellow-600 text-white rounded hover:bg-yellow-700 disabled:opacity-50"
            >
              Apply
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ColumnPicker;
